import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, signal } from '@angular/core';
import { catchError, Observable, of, tap } from 'rxjs';
import { environment } from '../../../environments/environment';
import { PaginationOptions } from '../models/common/pagination-options.model';
import { PaginatedScheduling } from '../models/scheduling/paginated-scheduling.model';
import { Scheduling } from '../models/scheduling/scheduling.model';
import { ServicesScheduling } from '../models/scheduling/services-scheduling.model';
import { CreateScheduling } from '../models/scheduling/create-scheduling.model';
import { ResponseCreateScheduling } from '../models/scheduling/reponse-create-scheduling.model';

@Injectable({ providedIn: 'root' })
export class SchedulingService {
    private readonly baseUrl = environment.apiUrl;
    private readonly transactionsUrl = `${this.baseUrl}corporate/`;

    readonly listScheduling = signal<Scheduling[] | null>(null);
    readonly totalScheduling = signal<number | null>(null);

    readonly currentPage = signal<number | null>(null);
    readonly pageSize = signal<number | null>(null);
    readonly totalPages = signal<number | null>(null);

    // ------------------------
    readonly listServicesByScheduling = signal<ServicesScheduling[] | null>(null);

    constructor(private http: HttpClient) { }

    private getSchedulingPaginated(options: PaginationOptions): Observable<PaginatedScheduling> {
        let params = new HttpParams()
            .set('page', options.page)
            .set('size', options.size);

        if (options.search) params = params.set('search', options.search);
        if (options.sortBy) params = params.set('sortBy', options.sortBy);
        if (options.sortDir) params = params.set('sortDir', options.sortDir);

        if (options.filters) {
            Object.entries(options.filters).forEach(([key, value]) => {
                params = params.set(key, value);
            });
        }

        return this.http.get<PaginatedScheduling>(
            `${this.transactionsUrl}bookings`,
            { params, withCredentials: true }
        );
    }

    private getServicesPaginated(): Observable<ServicesScheduling[]> {
        return this.http.get<ServicesScheduling[]>(
            `${this.transactionsUrl}bookings/services`,
            { withCredentials: true }
        );
    }

    createScheduling(scheduling: CreateScheduling): Observable<ResponseCreateScheduling> {
        return this.http.post<ResponseCreateScheduling>(
            `${this.transactionsUrl}bookings/paid`,
            scheduling,
            { withCredentials: true }
        );
    }

    processScheduling(id: string, isApproved: boolean): Observable<void> {
        return this.http.post<void>(
            `${this.transactionsUrl}bookings/${id}/${isApproved ? 'approve' : 'reject'}`,
            undefined,
            { withCredentials: true }
        );
    }

    loadSchedulingPaginated(options: PaginationOptions): Observable<PaginatedScheduling> {
        return this.getSchedulingPaginated(options).pipe(
            tap(data => {
                this.listScheduling.set(data.bookings);
                this.totalScheduling.set(data.totalElements);
                this.currentPage.set(data.currentPage);
                this.pageSize.set(data.pageSize);
                this.totalPages.set(data.totalPages);
            }),
            catchError(error => {
                console.error('Error loading paginated scheduling:', error);
                this.listScheduling.set([]);
                return of({
                    bookings: [],
                    totalElements: 0,
                    totalPages: 0,
                    currentPage: options.page,
                    pageSize: options.size
                });
            })
        );
    }

    loadServices(): Observable<ServicesScheduling[]> {
        return this.getServicesPaginated().pipe(
            tap(data => {
                this.listServicesByScheduling.set(data);
            }),
            catchError(error => {
                console.error('Error loading paginated services:', error);
                this.listServicesByScheduling.set([]);
                return of([]);
            })
        );
    }

}